import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
  ResponsiveContainer 
} from 'recharts';
import './Reports.css';

const Reports = ({ user }) => {
  const [activeTab, setActiveTab] = useState('trip_report');
  const [timeRange, setTimeRange] = useState('week');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    dailyActivity: [],
    vehicleStatus: [],
    vendorDistribution: [],
    tripTrends: [],
    performanceMetrics: {}
  });
  const [tripReportData, setTripReportData] = useState({
    trips: [],
    summary: {},
    vehicleSummary: [],
    overallTotals: {},
    filters: {}
  });
  const [vehicles, setVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [error, setError] = useState('');
  
  // Trip Report States
  const [tripReportFilters, setTripReportFilters] = useState({
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    vehicle_number: '',
    report_type: 'detailed'
  });
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [tripReportLoading, setTripReportLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchData();
    } else if (activeTab === 'trip_report') {
      fetchTripReport();
    }
  }, [activeTab, timeRange]);

  useEffect(() => {
    fetchVehiclesList();
  }, []);

  const fetchVehiclesList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/vehicles.php', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAvailableVehicles(data.data);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all required data
      const [vehiclesRes, tripsRes] = await Promise.all([
        fetch(process.env.REACT_APP_API_BASE_URL+'/vehicles.php', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(process.env.REACT_APP_API_BASE_URL+'/trips.php', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const vehiclesData = await vehiclesRes.json();
      const tripsData = await tripsRes.json();

      if (vehiclesData.success && tripsData.success) {
        setVehicles(vehiclesData.data);
        setTrips(tripsData.data);
        generateReportData(vehiclesData.data, tripsData.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (error) {
      setError('Error connecting to server');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTripReport = async () => {
    setTripReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_API_BASE_URL}/trips.php?report=custom_report&start_date=${tripReportFilters.start_date}&end_date=${tripReportFilters.end_date}&report_type=${tripReportFilters.report_type}`;
      
      if (tripReportFilters.vehicle_number) {
        url += `&vehicle_number=${tripReportFilters.vehicle_number}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        if (data.report_type === 'detailed') {
          setTripReportData({
            trips: data.data || [],
            summary: {},
            vehicleSummary: [],
            overallTotals: {},
            filters: data.filters || {}
          });
        } else if (data.report_type === 'summary') {
          setTripReportData({
            trips: [],
            summary: data.data?.overall_totals || {},
            vehicleSummary: data.data?.vehicle_summary || [],
            overallTotals: data.data?.overall_totals || {},
            filters: data.filters || {}
          });
        }
      } else {
        setError('Failed to fetch trip report');
      }
    } catch (error) {
      setError('Error fetching trip report');
      console.error('Error:', error);
    } finally {
      setTripReportLoading(false);
    }
  };

  const generateReportData = (vehiclesData, tripsData) => {
    const now = new Date();
    let days;
    switch(timeRange) {
      case 'week': days = 7; break;
      case 'month': days = 30; break;
      case 'quarter': days = 90; break;
      default: days = 7;
    }

    // Calculate date range
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days + 1);
    startDate.setHours(0, 0, 0, 0);

    // Filter trips within date range
    const recentTrips = tripsData.filter(trip => {
      const tripDate = new Date(trip.action_time);
      return tripDate >= startDate;
    });

    // 1. Daily Activity Overview
    const dailyActivity = [];
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayTrips = recentTrips.filter(trip => {
        const tripDate = new Date(trip.action_time);
        return tripDate.toDateString() === date.toDateString();
      });

      const dailyBoarded = dayTrips.filter(t => t.action === 'BOARD').length;
      const dailyUnboarded = dayTrips.filter(t => t.action === 'UNBOARD').length;
      
      // Calculate overdue for BOARD trips (more than 48 hours)
      const dailyOverdue = vehiclesData.filter(vehicle => {
        if (vehicle.current_status === 'boarded' && vehicle.last_boarded) {
          const boardedTime = new Date(vehicle.last_boarded);
          const hoursDiff = Math.abs(now - boardedTime) / 36e5;
          return hoursDiff > 48;
        }
        return false;
      }).length;

      dailyActivity.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        boarded: dailyBoarded,
        unboarded: dailyUnboarded,
        overdue: dailyOverdue
      });
    }

    // 2. Vehicle Status Distribution
    const totalVehicles = vehiclesData.length;
    const boardedCount = vehiclesData.filter(v => v.current_status === 'boarded').length;
    const unboardedCount = vehiclesData.filter(v => v.current_status === 'unboarded').length;
    
    const overdueCount = vehiclesData.filter(vehicle => {
      if (vehicle.current_status === 'boarded' && vehicle.last_boarded) {
        const boardedTime = new Date(vehicle.last_boarded);
        const hoursDiff = Math.abs(now - boardedTime) / 36e5;
        return hoursDiff > 48;
      }
      return false;
    }).length;

    const vehicleStatus = [
      { name: 'Boarded', value: boardedCount, color: '#10b981' },
      { name: 'Unboarded', value: unboardedCount, color: '#6b7280' },
      { name: 'Overdue', value: overdueCount, color: '#ef4444' },
      { name: 'Inactive', value: totalVehicles - boardedCount - unboardedCount, color: '#8b5cf6' }
    ];

    // 3. Vendor Distribution
    const vendorMap = {};
    vehiclesData.forEach(vehicle => {
      const vendor = vehicle.vendor || 'Unknown';
      if (!vendorMap[vendor]) {
        vendorMap[vendor] = {
          name: vendor,
          trips: 0,
          color: getColorForVendor(vendor)
        };
      }
      // Count trips for this vendor's vehicles
      vendorMap[vendor].trips += tripsData.filter(t => t.vehicle_id === vehicle.id).length;
    });

    const vendorDistribution = Object.values(vendorMap)
      .sort((a, b) => b.trips - a.trips)
      .slice(0, 5); // Top 5 vendors

    // 4. Monthly Trip Trends (last 6 months)
    const tripTrends = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthTrips = tripsData.filter(trip => {
        const tripDate = new Date(trip.action_time);
        return tripDate >= monthStart && tripDate <= monthEnd;
      });

      const completed = monthTrips.filter(t => t.action === 'UNBOARD').length;
      const overdue = monthTrips.filter(t => t.sla_status === 'overdue').length;
      
      // Calculate SLA compliance percentage
      const totalTrips = monthTrips.length;
      const onTimeTrips = totalTrips - overdue;
      const sla = totalTrips > 0 ? Math.round((onTimeTrips / totalTrips) * 100) : 0;

      tripTrends.push({
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        completed,
        overdue,
        sla
      });
    }

    // 5. Performance Metrics
    const totalTrips = recentTrips.length;
    const overdueTrips = recentTrips.filter(t => t.sla_status === 'overdue').length;
    const onTimeRate = totalTrips > 0 ? `${Math.round(((totalTrips - overdueTrips) / totalTrips) * 100)}%` : '0%';

    // Calculate average duration
    const completedTrips = recentTrips.filter(t => t.action === 'UNBOARD' && t.duration_minutes);
    const avgDuration = completedTrips.length > 0 
      ? Math.round(completedTrips.reduce((sum, trip) => sum + (trip.duration_minutes || 0), 0) / completedTrips.length)
      : 0;

    // Find peak hour (group by hour)
    const hourlyCounts = {};
    recentTrips.forEach(trip => {
      const hour = new Date(trip.action_time).getHours();
      const hourRange = `${hour.toString().padStart(2, '0')}:00`;
      hourlyCounts[hourRange] = (hourlyCounts[hourRange] || 0) + 1;
    });

    let peakHour = 'N/A';
    let maxTrips = 0;
    Object.entries(hourlyCounts).forEach(([hour, count]) => {
      if (count > maxTrips) {
        maxTrips = count;
        peakHour = hour;
      }
    });

    // Find busiest day
    const dayCounts = {};
    recentTrips.forEach(trip => {
      const day = new Date(trip.action_time).toLocaleDateString('en-US', { weekday: 'long' });
      dayCounts[day] = (dayCounts[day] || 0) + 1;
    });

    let busiestDay = 'N/A';
    let maxDayTrips = 0;
    Object.entries(dayCounts).forEach(([day, count]) => {
      if (count > maxDayTrips) {
        maxDayTrips = count;
        busiestDay = day;
      }
    });

    // Find top vendor
    let topVendor = 'N/A';
    let maxVendorTrips = 0;
    vendorDistribution.forEach(vendor => {
      if (vendor.trips > maxVendorTrips) {
        maxVendorTrips = vendor.trips;
        topVendor = vendor.name;
      }
    });

    const performanceMetrics = {
      totalTrips,
      onTimeRate,
      avgDuration: avgDuration > 0 ? `${Math.floor(avgDuration / 60)}h ${avgDuration % 60}m` : 'N/A',
      peakHour: peakHour !== 'N/A' ? `${peakHour} - ${(parseInt(peakHour.split(':')[0]) + 1).toString().padStart(2, '0')}:00` : 'N/A',
      busiestDay,
      topVendor
    };

    setReportData({
      dailyActivity,
      vehicleStatus,
      vendorDistribution,
      tripTrends,
      performanceMetrics
    });
    setLoading(false);
  };

  const getColorForVendor = (vendor) => {
    const colors = ['#1e3c72', '#2a5298', '#4361ee', '#4cc9f0', '#3a0ca3', '#4361ee', '#4895ef', '#4cc9f0'];
    const index = vendor.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      
      if (format === 'excel') {
        // Generate Excel export data
        const exportData = {
          summary: reportData.performanceMetrics,
          dailyActivity: reportData.dailyActivity,
          vehicleStatus: reportData.vehicleStatus,
          vendorDistribution: reportData.vendorDistribution,
          tripTrends: reportData.tripTrends
        };

        // Convert to CSV
        let csvContent = "data:text/csv;charset=utf-8,";
        
        // Summary
        csvContent += "SUMMARY\r\n";
        csvContent += "Metric,Value\r\n";
        Object.entries(exportData.summary).forEach(([key, value]) => {
          csvContent += `${key},${value}\r\n`;
        });
        csvContent += "\r\n\r\n";
        
        // Daily Activity
        csvContent += "DAILY ACTIVITY\r\n";
        csvContent += "Date,Boarded,Unboarded,Overdue\r\n";
        exportData.dailyActivity.forEach(row => {
          csvContent += `${row.date},${row.boarded},${row.unboarded},${row.overdue}\r\n`;
        });
        csvContent += "\r\n\r\n";
        
        // Vehicle Status
        csvContent += "VEHICLE STATUS\r\n";
        csvContent += "Status,Count\r\n";
        exportData.vehicleStatus.forEach(row => {
          csvContent += `${row.name},${row.value}\r\n`;
        });

        // Create download link
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `vehicle_tracking_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert('Report exported successfully as CSV');
      } else if (format === 'pdf') {
        // For PDF, we'll generate a printable version
        window.print();
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report');
    }
  };

  const handleTripReportExport = async (format) => {
    try {
      if (format === 'excel') {
        let csvContent = "data:text/csv;charset=utf-8,";
        
        if (tripReportFilters.report_type === 'detailed') {
          // Export detailed report
          csvContent += "Vehicle Number,Driver Name,Action,Date & Time,RTKM,HSD,Advance Expense,Destination,Duration (min),SLA Status,Notes\r\n";
          
          tripReportData.trips.forEach(trip => {
            csvContent += `${trip.vehicle_number || ''},`;
            csvContent += `${trip.driver_name || ''},`;
            csvContent += `${trip.action || ''},`;
            csvContent += `${trip.formatted_time || trip.action_time || ''},`;
            csvContent += `${trip.rtkm || 0},`;
            csvContent += `${trip.hsd || 0},`;
            csvContent += `${trip.advance_exp || 0},`;
            csvContent += `${trip.destination || ''},`;
            csvContent += `${trip.duration_minutes || ''},`;
            csvContent += `${trip.sla_status || ''},`;
            csvContent += `${trip.notes || ''}\r\n`;
          });
        } else {
          // Export summary report
          csvContent += "Vehicle Number,Driver Name,Vehicle Type,Vendor,Total Trips,Total RTKM,Total HSD (L),Total Advance (₹),Avg Duration (min),Overdue Trips\r\n";
          
          tripReportData.vehicleSummary.forEach(vehicle => {
            csvContent += `${vehicle.vehicle_number || ''},`;
            csvContent += `${vehicle.driver_name || ''},`;
            csvContent += `${vehicle.vehicle_type || ''},`;
            csvContent += `${vehicle.vendor || ''},`;
            csvContent += `${vehicle.total_trips || 0},`;
            csvContent += `${vehicle.total_rtkm || 0},`;
            csvContent += `${vehicle.total_hsd || 0},`;
            csvContent += `${vehicle.total_advance_exp || 0},`;
            csvContent += `${Math.round(vehicle.avg_duration || 0)},`;
            csvContent += `${vehicle.overdue_trips || 0}\r\n`;
          });
          
          // Add overall totals
          csvContent += "\r\n\r\nOVERALL TOTALS\r\n";
          csvContent += `Total Trips,${tripReportData.overallTotals.total_trips || 0}\r\n`;
          csvContent += `Total Vehicles Used,${tripReportData.overallTotals.total_vehicles_used || 0}\r\n`;
          csvContent += `Total RTKM,${tripReportData.overallTotals.total_rtkm || 0}\r\n`;
          csvContent += `Total HSD,${tripReportData.overallTotals.total_hsd || 0}\r\n`;
          csvContent += `Total Advance Expense,${tripReportData.overallTotals.total_advance_exp || 0}\r\n`;
          csvContent += `Average Fuel Efficiency,${(Number(tripReportData.overallTotals.avg_fuel_efficiency) || 0).toFixed(2)} km/L\r\n`;
          csvContent += `Overdue Trips,${tripReportData.overallTotals.overdue_trips || 0}\r\n`;
        }
        
        // Create download link
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `trip_report_${tripReportFilters.report_type}_${tripReportFilters.start_date}_to_${tripReportFilters.end_date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        alert('Trip report exported successfully as CSV');
      } else if (format === 'pdf') {
        window.print();
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export trip report');
    }
  };

  const getPeakHoursData = () => {
    // Analyze trips by hour for the current day
    const today = new Date().toDateString();
    const todayTrips = trips.filter(trip => {
      const tripDate = new Date(trip.action_time);
      return tripDate.toDateString() === today;
    });

    const hourlyGroups = {
      morning: { trips: 0, hours: '08:00 - 10:00', icon: '🌅', label: 'Morning Peak' },
      afternoon: { trips: 0, hours: '14:00 - 16:00', icon: '☀️', label: 'Afternoon Peak' },
      evening: { trips: 0, hours: '16:00 - 18:00', icon: '🌇', label: 'Evening Peak' },
      night: { trips: 0, hours: '22:00 - 06:00', icon: '🌙', label: 'Night Shift' }
    };

    todayTrips.forEach(trip => {
      const hour = new Date(trip.action_time).getHours();
      
      if (hour >= 8 && hour < 10) {
        hourlyGroups.morning.trips++;
      } else if (hour >= 14 && hour < 16) {
        hourlyGroups.afternoon.trips++;
      } else if (hour >= 16 && hour < 18) {
        hourlyGroups.evening.trips++;
      } else if (hour >= 22 || hour < 6) {
        hourlyGroups.night.trips++;
      }
    });

    const totalTodayTrips = todayTrips.length;
    
    return Object.values(hourlyGroups).filter(group => group.trips > 0).map(group => ({
      ...group,
      percentage: totalTodayTrips > 0 ? `${Math.round((group.trips / totalTodayTrips) * 100)}% of daily volume` : '0%'
    }));
  };

  const handleTripReportFilterChange = (e) => {
    const { name, value } = e.target;
    setTripReportFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyTripReportFilters = () => {
    fetchTripReport();
  };

  const resetTripReportFilters = () => {
    setTripReportFilters({
      start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      vehicle_number: '',
      report_type: 'detailed'
    });
    setTimeout(() => {
      fetchTripReport();
    }, 100);
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="reports-loading">
        <div className="loading-spinner"></div>
        <p>Generating reports...</p>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <div>
          <h1>Reports & Analytics</h1>
          <p className="page-subtitle">Comprehensive insights and performance metrics</p>
        </div>
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'trip_report' ? 'active' : ''}`}
            onClick={() => setActiveTab('trip_report')}
          >
            🚛 Trip Report
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <span>❌</span> {error}
        </div>
      )}

      {activeTab === 'overview' && (
        <>
          <div className="header-actions">
            <div className="time-range-selector">
              <label>Time Range:</label>
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value)}
                className="range-select"
              >
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 90 Days</option>
              </select>
            </div>
            <div className="export-buttons">
              <button 
                className="btn-secondary"
                onClick={() => handleExport('pdf')}
              >
                <span>📄</span> PDF
              </button>
              <button 
                className="btn-secondary"
                onClick={() => handleExport('excel')}
              >
                <span>📊</span> Excel
              </button>
              <button 
                className="btn-primary"
                onClick={fetchData}
              >
                <span>🔄</span> Refresh
              </button>
            </div>
          </div>

          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon" style={{background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'}}>
                📋
              </div>
              <div className="metric-content">
                <h3>Total Trips</h3>
                <p className="metric-value">{reportData.performanceMetrics.totalTrips.toLocaleString()}</p>
                <p className="metric-trend">Period: {timeRange === 'week' ? '7 days' : timeRange === 'month' ? '30 days' : '90 days'}</p>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                ✅
              </div>
              <div className="metric-content">
                <h3>On-Time Rate</h3>
                <p className="metric-value">{reportData.performanceMetrics.onTimeRate}</p>
                <p className="metric-trend">Based on SLA compliance</p>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                ⏱️
              </div>
              <div className="metric-content">
                <h3>Avg Duration</h3>
                <p className="metric-value">{reportData.performanceMetrics.avgDuration}</p>
                <p className="metric-trend">For completed trips</p>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                🏆
              </div>
              <div className="metric-content">
                <h3>Top Vendor</h3>
                <p className="metric-value">{reportData.performanceMetrics.topVendor}</p>
                <p className="metric-trend">Most active vendor</p>
              </div>
            </div>
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <div className="chart-header">
                <h3>Daily Activity Overview</h3>
                <span className="chart-subtitle">Boarding vs Unboarding Operations</span>
              </div>
              {reportData.dailyActivity.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData.dailyActivity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: 'none', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        background: 'white'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="boarded" name="Boarded" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unboarded" name="Unboarded" fill="#6b7280" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="overdue" name="Overdue" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No activity data available</div>
              )}
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Vehicle Status Distribution</h3>
                <span className="chart-subtitle">Current operational status</span>
              </div>
              {reportData.vehicleStatus.filter(v => v.value > 0).length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={reportData.vehicleStatus.filter(v => v.value > 0)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {reportData.vehicleStatus.filter(v => v.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [value, 'Count']}
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: 'none', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        background: 'white'
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No vehicle data available</div>
              )}
            </div>

            <div className="chart-card full-width">
              <div className="chart-header">
                <h3>Trip Performance Trends</h3>
                <span className="chart-subtitle">Monthly overview with SLA compliance</span>
              </div>
              {reportData.tripTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={reportData.tripTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: 'none', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        background: 'white'
                      }}
                    />
                    <Legend />
                    <Area 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="completed" 
                      name="Completed Trips"
                      stackId="1"
                      stroke="#1e3c72" 
                      fill="url(#colorCompleted)" 
                      fillOpacity={0.8}
                    />
                    <Area 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="overdue" 
                      name="Overdue Trips"
                      stackId="1"
                      stroke="#ef4444" 
                      fill="url(#colorOverdue)" 
                      fillOpacity={0.8}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone"
                      dataKey="sla"
                      name="SLA %"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <defs>
                      <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1e3c72" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#1e3c72" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorOverdue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No trend data available</div>
              )}
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Vendor Performance</h3>
                <span className="chart-subtitle">Trip distribution by vendor</span>
              </div>
              {reportData.vendorDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={reportData.vendorDistribution}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={90} />
                    <Tooltip 
                      formatter={(value) => [value, 'Trips']}
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: 'none', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        background: 'white'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="trips" 
                      name="Total Trips"
                      radius={[0, 4, 4, 0]}
                    >
                      {reportData.vendorDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-data">No vendor data available</div>
              )}
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3>Peak Hours Analysis</h3>
                <span className="chart-subtitle">Busiest times for operations</span>
              </div>
              <div className="peak-hours">
                {getPeakHoursData().length > 0 ? (
                  getPeakHoursData().map((peak, index) => (
                    <div key={index} className="peak-hour-item">
                      <div className="peak-time">
                        <span className="time-icon">{peak.icon}</span>
                        <div>
                          <div className="time-slot">{peak.hours}</div>
                          <div className="peak-label">{peak.label}</div>
                        </div>
                      </div>
                      <div className="peak-stats">
                        <div className="stat">{peak.trips} trips</div>
                        <div className="percentage">{peak.percentage}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data">No peak hour data available</div>
                )}
              </div>
            </div>
          </div>

          <div className="insights-card">
            <h3>Key Insights & Recommendations</h3>
            <div className="insights-grid">
              <div className="insight-item positive">
                <div className="insight-header">
                  <span className="insight-icon">📈</span>
                  <h4>Positive Trend</h4>
                </div>
                <p>On-time performance: {reportData.performanceMetrics.onTimeRate}. Continue with current scheduling practices.</p>
              </div>
              <div className="insight-item warning">
                <div className="insight-header">
                  <span className="insight-icon">⚠️</span>
                  <h4>Attention Needed</h4>
                </div>
                <p>{reportData.vehicleStatus.find(v => v.name === 'Overdue')?.value || 0} vehicles are overdue. Schedule maintenance checks.</p>
              </div>
              <div className="insight-item suggestion">
                <div className="insight-header">
                  <span className="insight-icon">💡</span>
                  <h4>Suggestion</h4>
                </div>
                <p>Peak operation time: {reportData.performanceMetrics.peakHour}. Consider optimizing resource allocation.</p>
              </div>
              <div className="insight-item action">
                <div className="insight-header">
                  <span className="insight-icon">🎯</span>
                  <h4>Action Required</h4>
                </div>
                <p>Busiest day: {reportData.performanceMetrics.busiestDay}. Plan additional staff for better coverage.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'trip_report' && (
        <div className="trip-report-container">
          {/* Filters Section */}
          <div className="filters-card">
            <h3>Filter Trip Reports</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={tripReportFilters.start_date}
                  onChange={handleTripReportFilterChange}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="end_date"
                  value={tripReportFilters.end_date}
                  onChange={handleTripReportFilterChange}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>Vehicle Number</label>
                <select
                  name="vehicle_number"
                  value={tripReportFilters.vehicle_number}
                  onChange={handleTripReportFilterChange}
                  className="filter-select"
                >
                  <option value="">All Vehicles</option>
                  {availableVehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.vehicle_number}>
                      {vehicle.vehicle_number} - {vehicle.driver_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Report Type</label>
                <select
                  name="report_type"
                  value={tripReportFilters.report_type}
                  onChange={handleTripReportFilterChange}
                  className="filter-select"
                >
                  <option value="detailed">Detailed Report</option>
                  <option value="summary">Summary Report</option>
                </select>
              </div>
              <div className="filter-actions">
                <button className="btn-primary" onClick={applyTripReportFilters}>
                  Apply Filters
                </button>
                <button className="btn-secondary" onClick={resetTripReportFilters}>
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards - Only show for detailed report or always? Let's show overall totals for both */}
          <div className="metrics-grid">
            {tripReportFilters.report_type === 'detailed' ? (
              // For detailed report, show summary from the trips data
              <>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'}}>
                    🚛
                  </div>
                  <div className="metric-content">
                    <h3>Total Trips</h3>
                    <p className="metric-value">{tripReportData.trips.filter(t => t.action === 'BOARD').length}</p>
                    <p className="metric-trend">Boarding trips</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                    📍
                  </div>
                  <div className="metric-content">
                    <h3>Total RTKM</h3>
                    <p className="metric-value">{tripReportData.trips.filter(t => t.action === 'BOARD').reduce((sum, t) => sum + (parseFloat(t.rtkm) || 0), 0).toLocaleString()}</p>
                    <p className="metric-trend">Total kilometers</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                    ⛽
                  </div>
                  <div className="metric-content">
                    <h3>Total HSD</h3>
                    <p className="metric-value">{tripReportData.trips.filter(t => t.action === 'BOARD').reduce((sum, t) => sum + (parseFloat(t.hsd) || 0), 0).toLocaleString()} L</p>
                    <p className="metric-trend">Fuel consumption</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                    💰
                  </div>
                  <div className="metric-content">
                    <h3>Total Advance</h3>
                    <p className="metric-value">₹{tripReportData.trips.filter(t => t.action === 'BOARD').reduce((sum, t) => sum + (parseFloat(t.advance_exp) || 0), 0).toLocaleString()}</p>
                    <p className="metric-trend">Expenses</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                    📊
                  </div>
                  <div className="metric-content">
                    <h3>Fuel Efficiency</h3>
                    <p className="metric-value">
                      {(() => {
                        const totalRTKM = tripReportData.trips.filter(t => t.action === 'BOARD').reduce((sum, t) => sum + (parseFloat(t.rtkm) || 0), 0);
                        const totalHSD = tripReportData.trips.filter(t => t.action === 'BOARD').reduce((sum, t) => sum + (parseFloat(t.hsd) || 0), 0);
                        const efficiency = totalHSD > 0 ? (totalRTKM / totalHSD).toFixed(2) : '0';
                        return `${efficiency} km/L`;
                      })()}
                    </p>
                    <p className="metric-trend">RTKM per liter</p>
                  </div>
                </div>
              </>
            ) : (
              // For summary report, use the overall totals from API
              <>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'}}>
                    🚛
                  </div>
                  <div className="metric-content">
                    <h3>Total Trips</h3>
                    <p className="metric-value">{tripReportData.overallTotals.total_trips || 0}</p>
                    <p className="metric-trend">Boarding trips</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                    📍
                  </div>
                  <div className="metric-content">
                    <h3>Total RTKM</h3>
                    <p className="metric-value">{(tripReportData.overallTotals.total_rtkm || 0).toLocaleString()}</p>
                    <p className="metric-trend">Total kilometers</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                    ⛽
                  </div>
                  <div className="metric-content">
                    <h3>Total HSD</h3>
                    <p className="metric-value">{(tripReportData.overallTotals.total_hsd || 0).toLocaleString()} L</p>
                    <p className="metric-trend">Fuel consumption</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                    💰
                  </div>
                  <div className="metric-content">
                    <h3>Total Advance</h3>
                    <p className="metric-value">₹{(tripReportData.overallTotals.total_advance_exp || 0).toLocaleString()}</p>
                    <p className="metric-trend">Expenses</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                    📊
                  </div>
                  <div className="metric-content">
                    <h3>Fuel Efficiency</h3>
                    <p className="metric-value">{(Number(tripReportData.overallTotals.avg_fuel_efficiency) || 0).toFixed(2)} km/L</p>
                    <p className="metric-trend">RTKM per liter</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Export Button */}
          <div className="export-section">
            <button className="btn-secondary" onClick={() => handleTripReportExport('excel')}>
              📊 Export to Excel
            </button>
            <button className="btn-secondary" onClick={() => handleTripReportExport('pdf')}>
              📄 Export to PDF
            </button>
          </div>

          {/* Data Display */}
          {tripReportLoading ? (
            <div className="loading-spinner-container">
              <div className="loading-spinner"></div>
              <p>Loading trip data...</p>
            </div>
          ) : (
            <>
              {tripReportFilters.report_type === 'detailed' ? (
                /* Detailed Report - Show individual trips */
                <div className="trip-table-container">
                  <h3>Trip Details</h3>
                  {tripReportData.trips && tripReportData.trips.length > 0 ? (
                    <div className="table-responsive">
                      <table className="trip-report-table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Vehicle No.</th>
                            <th>Driver Name</th>
                            <th>Action</th>
                            <th>Date & Time</th>
                            <th>RTKM</th>
                            <th>HSD (L)</th>
                            <th>Advance Exp (₹)</th>
                            <th>Destination</th>
                            <th>Duration</th>
                            <th>SLA Status</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tripReportData.trips.map((trip, index) => (
                            <tr key={trip.id || index}>
                              <td>{index + 1}</td>
                              <td>{trip.vehicle_number}</td>
                              <td>{trip.driver_name}</td>
                              <td>
                                <span className={`action-badge ${trip.action === 'BOARD' ? 'board' : 'unboard'}`}>
                                  {trip.action}
                                </span>
                              </td>
                              <td>{trip.formatted_time || new Date(trip.action_time).toLocaleString()}</td>
                              <td>{trip.rtkm || 0}</td>
                              <td>{trip.hsd || 0}</td>
                              <td>₹{trip.advance_exp || 0}</td>
                              <td>{trip.destination || '-'}</td>
                              <td>
                                {trip.duration_minutes ? 
                                  `${Math.floor(trip.duration_minutes / 60)}h ${trip.duration_minutes % 60}m` : 
                                  '-'
                                }
                              </td>
                              <td>
                                <span className={`sla-badge ${trip.sla_status}`}>
                                  {trip.sla_status === 'overdue' ? 'Overdue' : 
                                   trip.sla_status === 'active' ? 'Active' : 'On Time'}
                                </span>
                              </td>
                              <td>{trip.notes || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <p>No trips found for the selected filters.</p>
                      <p>Try adjusting your date range or vehicle selection.</p>
                    </div>
                  )}
                </div>
              ) : (
                /* Summary Report - Show vehicle-wise summary */
                <div className="trip-table-container">
                  <h3>Vehicle-wise Summary</h3>
                  {tripReportData.vehicleSummary && tripReportData.vehicleSummary.length > 0 ? (
                    <div className="table-responsive">
                      <table className="trip-report-table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Vehicle No.</th>
                            <th>Driver Name</th>
                            <th>Vehicle Type</th>
                            <th>Vendor</th>
                            <th>Total Trips</th>
                            <th>Total RTKM</th>
                            <th>Total HSD (L)</th>
                            <th>Total Advance (₹)</th>
                            <th>Avg Duration</th>
                            <th>Overdue Trips</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tripReportData.vehicleSummary.map((vehicle, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{vehicle.vehicle_number}</td>
                              <td>{vehicle.driver_name}</td>
                              <td>{vehicle.vehicle_type || '-'}</td>
                              <td>{vehicle.vendor || '-'}</td>
                              <td>{vehicle.total_trips || 0}</td>
                              <td>{vehicle.total_rtkm || 0}</td>
                              <td>{vehicle.total_hsd || 0}</td>
                              <td>₹{vehicle.total_advance_exp || 0}</td>
                              <td>
                                {vehicle.avg_duration ? 
                                  `${Math.floor(vehicle.avg_duration / 60)}h ${Math.round(vehicle.avg_duration % 60)}m` : 
                                  '-'
                                }
                              </td>
                              <td>
                                <span className={`sla-badge ${vehicle.overdue_trips > 0 ? 'overdue' : 'on_time'}`}>
                                  {vehicle.overdue_trips || 0}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <p>No summary data found for the selected filters.</p>
                      <p>Try adjusting your date range or vehicle selection.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;